import React from 'react';
import MuiModal from '../components/mui-modal';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { Form, Formik } from 'formik';
import { MonHocService } from '../../../services/mon-hoc/monHocService';
import {
    clearSelectedMonHoc,
    insertMonHocSuccess,
    updateMonHocSuccess
} from '../../../faetures/mon-hoc/monHocSlice';
import { MonHocSchema } from './createOrEditMonHocModal.validate';
import { BSKT_MonHoc } from '../../../services/mon-hoc/models/MonHocModels';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    TextField,
    Typography,
    Stack
} from '@mui/material';

interface ICreateOrEditMonHocModalProps {
    model: BSKT_MonHoc;
    isShow: boolean;
    title?: string;
    onClose: () => void;
    onSave: (model: BSKT_MonHoc) => void;
}

const CreateOrEditMonHocModal = (props: ICreateOrEditMonHocModalProps) => {
    const { isShow, onClose } = props;
    const dispatch = useAppDispatch();
    const { createOrEditModel } = useAppSelector((state) => state.monhoc);

    const handleSubmit = (values: BSKT_MonHoc) => {
        if (values.ID_mon === undefined || values.ID_mon <= 0) {
            MonHocService.insert(values).then((response) => {
                if (response) {
                    dispatch(insertMonHocSuccess(response));
                    dispatch(clearSelectedMonHoc());
                }
            });
        } else {
            MonHocService.update(values).then((response) => {
                if (response) {
                    dispatch(updateMonHocSuccess(response));
                    dispatch(clearSelectedMonHoc());
                }
            });
        }
    };

    const handleClose = () => {
        dispatch(clearSelectedMonHoc());
        onClose();
    };

    const isEdit = !!createOrEditModel.ID_mon && createOrEditModel.ID_mon > 0;

    return (
        <MuiModal
            isOpen={isShow}
            onClose={handleClose}
            title={isEdit ? 'Chỉnh sửa môn học' : 'Thêm mới môn học'}
        >
            <Formik
                initialValues={createOrEditModel}
                validationSchema={MonHocSchema}
                onSubmit={(values, hander) => {
                    handleSubmit(values);
                    hander.setSubmitting(false);
                    hander.resetForm();
                }}
                validateOnChange={false}
                validateOnBlur={false}
            >
                {({ values, handleChange, touched, errors, resetForm, isSubmitting }) => (
                    <Form>
                        {/* Mô tả nhỏ */}
                        <Box mb={2}>
                            <Typography variant="body2" color="text.secondary">
                                Nhập thông tin mã môn học, tên môn, số tín chỉ, lý thuyết và
                                thực hành. Các trường có dấu{' '}
                                <span style={{ color: '#e53935' }}>*</span> là bắt buộc.
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                borderRadius: 2,
                                border: '1px solid #f0f0f0',
                                p: 2.5,
                                backgroundColor: '#fafafa',
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                columnGap: 2,
                                rowGap: 2
                            }}
                        >
                            {/* Mã môn học */}
                            <FormControl
                                fullWidth
                                error={!!(touched.Ky_hieu && errors.Ky_hieu)}
                                size="small"
                            >
                                <FormLabel>
                                    Mã môn học <span className="text-danger">*</span>
                                </FormLabel>
                                <TextField
                                    name="Ky_hieu"
                                    value={values.Ky_hieu}
                                    onChange={handleChange}
                                    size="small"
                                />
                                {touched.Ky_hieu && errors.Ky_hieu && (
                                    <FormHelperText>{errors.Ky_hieu}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Tên môn học – cho full width 2 cột trên màn hình lớn */}
                            <FormControl
                                fullWidth
                                error={!!(touched.Ten_mon && errors.Ten_mon)}
                                size="small"
                                sx={{ gridColumn: { xs: '1 / -1', md: '1 / -1' } }}
                            >
                                <FormLabel>
                                    Tên môn học <span className="text-danger">*</span>
                                </FormLabel>
                                <TextField
                                    name="Ten_mon"
                                    value={values.Ten_mon}
                                    onChange={handleChange}
                                    size="small"
                                />
                                {touched.Ten_mon && errors.Ten_mon && (
                                    <FormHelperText>{errors.Ten_mon}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Số tín chỉ */}
                            <FormControl
                                fullWidth
                                error={!!(touched.So_tin_chi && errors.So_tin_chi)}
                                size="small"
                            >
                                <FormLabel>
                                    Số tín chỉ <span className="text-danger">*</span>
                                </FormLabel>
                                <TextField
                                    name="So_tin_chi"
                                    type="number"
                                    value={values.So_tin_chi}
                                    onChange={handleChange}
                                    size="small"
                                />
                                {touched.So_tin_chi && errors.So_tin_chi && (
                                    <FormHelperText>{errors.So_tin_chi}</FormHelperText>
                                )}
                            </FormControl>

                            {/* Lý thuyết */}
                            <FormControl fullWidth size="small">
                                <FormLabel>Lý thuyết</FormLabel>
                                <TextField
                                    name="Ly_thuyet"
                                    type="number"
                                    value={values.Ly_thuyet}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>

                            {/* Thực hành */}
                            <FormControl fullWidth size="small">
                                <FormLabel>Thực hành</FormLabel>
                                <TextField
                                    name="Thuc_hanh"
                                    type="number"
                                    value={values.Thuc_hanh}
                                    onChange={handleChange}
                                    size="small"
                                />
                            </FormControl>
                        </Box>

                        {/* Footer buttons */}
                        <Box mt={2}>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={2}
                            >
                                <Typography variant="caption" color="text.secondary">
                                    <span style={{ color: '#e53935' }}>*</span> Trường bắt buộc nhập
                                </Typography>

                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="flex-end"
                                    gap={1}
                                >
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        type="submit"
                                        size="small"
                                        disabled={isSubmitting}
                                    >
                                        {isEdit ? 'Cập nhật' : 'Lưu'}
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            resetForm();
                                            handleClose();
                                        }}
                                    >
                                        Đóng
                                    </Button>
                                </Box>
                            </Stack>
                        </Box>
                    </Form>
                )}
            </Formik>
        </MuiModal>
    );
};

export default CreateOrEditMonHocModal;